import { Box, Menu, MenuItem, ListItemText, Tooltip } from "@mui/material";
import { FC, useState, MouseEvent } from "react";
import { PosButton } from "../../../atoms";
import { Redo, KeyboardArrowDown, Check } from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { updateCartSessionItem } from "../../../../redux/actions/cartAction";
import { CartSliceAction } from "../../../../redux/slice/cartSlice";
import { cloneDeep } from "lodash";
import { Colors } from "../../../../configs/colors";

const ServiceActionButtons: FC = () => {
    const dispatch = useAppDispatch();
    const { selectedCartItem } = useAppSelector((state) => state.cart);

    // Local state for immediate UI feedback, but primarily driven by selectedCartItem
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleMenuItemClick = (option: "Press Only" | "Clean Only" | "Alteration Only") => {
        if (selectedCartItem) {
            const clonedCartItem = cloneDeep(selectedCartItem);
            clonedCartItem.pressOnly = option === "Press Only";
            clonedCartItem.cleanOnly = option === "Clean Only";
            clonedCartItem.alterationOnly = option === "Alteration Only";
            dispatch(updateCartSessionItem({ cartItem: clonedCartItem }));
        }
        handleClose();
    };

    const handleRedoClick = () => {
        dispatch(CartSliceAction.setIsRedoModalOpen(true));
    };

    const getButtonText = () => {
        if (!selectedCartItem) return "Processing Type";
        if (selectedCartItem.pressOnly) return "Press Only";
        if (selectedCartItem.cleanOnly) return "Clean Only";
        if (selectedCartItem.alterationOnly) return "Alteration Only";
        return "Processing Type";
    }

    const currentSelection = getButtonText();
    // "Alteration Only" only makes sense once the item actually has an
    // alteration attached - otherwise there'd be nothing for the zeroed
    // base price to be "instead of".
    const hasAlterations = !!selectedCartItem?.alterationItems?.length;

    return (
        <Box
            sx={{
                height: "5.857142857142857rem",
                flex: 1,
                display: "flex",
                alignItems: "center",
                borderRadius: "1.43rem",
                overflow: "hidden",
                boxShadow: "0 6px 16px rgba(0, 0, 0, 0.12)",
            }}
        >
            <PosButton
                variantType="secondary"
                name="processing-type-trigger"
                onClick={handleClick}
                disabled={!selectedCartItem}
                sx={{
                    height: "100%",
                    width: "100%",
                    borderRadius: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "0 1rem",
                    gap: "0.4rem",
                    whiteSpace: "nowrap",
                    "&.Mui-disabled": {
                        background: Colors.MAIN_GRAY,
                        color: Colors.WHITE,
                        opacity: 1,
                        cursor: "not-allowed",
                        pointerEvents: "auto"
                    }
                }}
            >
                {selectedCartItem ? (
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", minWidth: 0, overflow: "hidden" }}>
                        <Box
                            component="span"
                            sx={{
                                fontSize: "0.71rem",
                                fontWeight: 500,
                                letterSpacing: "0.02rem",
                                textTransform: "uppercase",
                                color: "rgba(255, 255, 255, 0.7)",
                                maxWidth: "100%",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            Processing Type
                        </Box>
                        <Box component="span" sx={{ fontSize: "1.14rem", fontWeight: 600, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {currentSelection}
                        </Box>
                    </Box>
                ) : (
                    <Box
                        component="span"
                        sx={{
                            fontSize: "1.14rem",
                            fontWeight: 500,
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        {currentSelection}
                    </Box>
                )}
                <KeyboardArrowDown sx={{ fontSize: "1.5rem", color: "rgba(255, 255, 255, 0.9)", flexShrink: 0 }} />
            </PosButton>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                sx={{
                    "& .MuiPaper-root": {
                        borderRadius: "1.57rem",
                        border: `1px solid ${Colors.ACCENT_GRAY}`,
                        boxShadow: "0 14px 34px rgba(0, 0, 0, 0.18)",
                        width: anchorEl ? `${anchorEl.clientWidth}px` : "auto",
                        overflow: "hidden",
                    }
                }}
            >
                <MenuItem
                    onClick={() => handleMenuItemClick("Press Only")}
                    sx={{ gap: "0.86rem", padding: "1rem 1.2rem" }}
                >
                    <Box
                        sx={{
                            width: "0.85rem",
                            height: "0.85rem",
                            borderRadius: "50%",
                            background: Colors.PRIMARY,
                            opacity: 0.8,
                            flexShrink: 0,
                        }}
                    />
                    <ListItemText primary="Press Only" primaryTypographyProps={{ fontSize: "1.2rem", fontWeight: 500, color: Colors.MAIN_GRAY }} />
                    {currentSelection === "Press Only" && <Check sx={{ fontSize: "1.3rem", color: Colors.PRIMARY }} />}
                </MenuItem>
                <MenuItem
                    onClick={() => handleMenuItemClick("Clean Only")}
                    sx={{ gap: "0.86rem", padding: "1rem 1.2rem", borderTop: `1px solid ${Colors.BACKGROUND_GRAY}` }}
                >
                    <Box
                        sx={{
                            width: "0.85rem",
                            height: "0.85rem",
                            borderRadius: "50%",
                            background: Colors.PRIMARY,
                            opacity: 0.8,
                            flexShrink: 0,
                        }}
                    />
                    <ListItemText primary="Clean Only" primaryTypographyProps={{ fontSize: "1.2rem", fontWeight: 500, color: Colors.MAIN_GRAY }} />
                    {currentSelection === "Clean Only" && <Check sx={{ fontSize: "1.3rem", color: Colors.PRIMARY }} />}
                </MenuItem>
                <Tooltip title={hasAlterations ? "" : "Add an alteration first"} placement="right">
                    <span>
                        <MenuItem
                            onClick={() => handleMenuItemClick("Alteration Only")}
                            disabled={!hasAlterations}
                            sx={{ gap: "0.86rem", padding: "1rem 1.2rem", borderTop: `1px solid ${Colors.BACKGROUND_GRAY}` }}
                        >
                            <Box
                                sx={{
                                    width: "0.85rem",
                                    height: "0.85rem",
                                    borderRadius: "50%",
                                    background: Colors.PRIMARY,
                                    opacity: 0.8,
                                    flexShrink: 0,
                                }}
                            />
                            <ListItemText primary="Alteration Only" primaryTypographyProps={{ fontSize: "1.2rem", fontWeight: 500, color: Colors.MAIN_GRAY }} />
                            {currentSelection === "Alteration Only" && <Check sx={{ fontSize: "1.3rem", color: Colors.PRIMARY }} />}
                        </MenuItem>
                    </span>
                </Tooltip>
            </Menu>

            <Box sx={{ width: "1px", height: "3.43rem", background: Colors.WHITE, opacity: 0.25, flexShrink: 0 }} />

            <PosButton
                variantType="secondary"
                name="redo-trigger"
                disabled={!selectedCartItem}
                onClick={handleRedoClick}
                sx={{
                    height: "100%",
                    width: "100%",
                    borderRadius: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem",
                    fontSize: "1.14rem",
                    "&.Mui-disabled": {
                        background: Colors.MAIN_GRAY,
                        color: Colors.WHITE,
                        opacity: 1,
                        cursor: "not-allowed",
                        pointerEvents: "auto"
                    }
                }}
            >
                <Redo sx={{ fontSize: "1.5rem" }} />
                Redo
            </PosButton>
        </Box>
    );
};

export default ServiceActionButtons;
